// Müşteri toplantısı ekleme/düzenleme modal'ı
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { FiX, FiPlus } from 'react-icons/fi';
import { customerService } from '../../services/customerService';
import { userService } from '../../services/userService';
import toast from 'react-hot-toast';
import { parseDateTimeForInputs } from '../../utils/dateUtils';

const CustomerMeetingModal = ({ customerId, meeting, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  // Kullanıcıları getir
  const { data: usersData } = useQuery(
    'users',
    () => userService.getUsers(),
    { enabled: true }
  );

  const users = usersData?.users || [];

  // Toplantı oluşturma/güncelleme mutation'ı
  const saveMeetingMutation = useMutation(
    (data) => {
      if (meeting) {
        // Toplantı güncelleme
        return customerService.updateCustomerMeeting(meeting.id, data);
      } else {
        // Yeni toplantı ekleme
        return customerService.addCustomerMeeting(customerId, data);
      }
    },
    {
      onSuccess: (data) => {
        toast.success(meeting ? 'Toplantı güncellendi' : 'Toplantı eklendi');
        queryClient.invalidateQueries(['customer', customerId]);
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'İşlem başarısız');
      }
    }
  );

  // Form'u toplantı verileriyle doldur
  useEffect(() => {
    if (meeting) {
      setValue('meeting_subject', meeting.meeting_subject);
      setValue('meeting_description', meeting.meeting_description);
      setValue('meeting_location', meeting.meeting_location);
      setValue('meeting_participation', meeting.meeting_participation);
      setValue('meeting_type', meeting.meeting_type);
      setValue('meeting_reason', meeting.meeting_reason);
      setValue('meeting_status', meeting.meeting_status);
      
      // Tarih ve saatleri ayır (timezone dönüşümü yapmadan)
      if (meeting.meeting_start_date) {
        const { date, startTime } = parseDateTimeForInputs(meeting.meeting_start_date);
        setValue('meeting_date', date);
        setValue('meeting_start_time', startTime);
      }
      
      if (meeting.meeting_end_date) {
        const { startTime: endTime } = parseDateTimeForInputs(meeting.meeting_end_date);
        setValue('meeting_end_time', endTime);
      }

      // Katılımcıları parse et
      if (meeting.meeting_participation) {
        try {
          const participants = JSON.parse(meeting.meeting_participation);
          setSelectedUsers(participants);
        } catch (error) {
          console.error('Katılımcı verileri parse edilemedi:', error);
        }
      }
    }
  }, [meeting, setValue]);

  // Kullanıcı ekleme
  const handleAddUser = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      // Sadece gerekli bilgileri kaydet
      const userInfo = {
        id: user.id,
        user_uuid: user.user_uuid,
        user_fullname: user.user_fullname
      };
      setSelectedUsers([...selectedUsers, userInfo]);
    }
    setShowUserDropdown(false);
  };

  // Kullanıcı silme
  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Form gönderimi
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Tarih ve saatleri birleştir
      const formData = {
        ...data,
        meeting_start_date: `${data.meeting_date}T${data.meeting_start_time}:00`,
        meeting_end_date: `${data.meeting_date}T${data.meeting_end_time}:00`,
        meeting_participation: JSON.stringify(selectedUsers),
        meeting_status: 'Planlandı', // Varsayılan durum
        meeting_for: 'customer' // Müşteri toplantısı olduğunu belirt
      };
      
      // Tarih ve saat alanlarını kaldır
      delete formData.meeting_date;
      delete formData.meeting_start_time;
      delete formData.meeting_end_time;
      
      await saveMeetingMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Form gönderim hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl">
        {/* Modal header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">
              {meeting ? 'Toplantı Düzenle' : 'Yeni Toplantı'}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Toplantı nedeni ve konusu */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:col-span-2">
              <div>
                <label htmlFor="meeting_reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Toplantı Konusu *
                </label>
                <select
                  id="meeting_reason"
                  {...register('meeting_reason', { required: 'Toplantı konusu gerekli' })}
                  className={`input-field ${errors.meeting_reason ? 'input-error' : ''}`}
                >
                  <option value="">Konu seçin</option>
                  <option value="Tanışma Toplantısı">Tanışma Toplantısı</option>
                  <option value="Proje Toplantısı">Proje Toplantısı</option>
                  <option value="Satış Toplantısı">Satış Toplantısı</option>
                  <option value="Genel Toplantı">Genel Toplantı</option>
                </select>
                {errors.meeting_reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.meeting_reason.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="meeting_subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Toplantı Başlığı *
                </label>
                <input
                  id="meeting_subject"
                  type="text"
                  {...register('meeting_subject', { required: 'Toplantı başlığı gerekli' })}
                  className={`input-field ${errors.meeting_subject ? 'input-error' : ''}`}
                  placeholder="Toplantı başlığını girin"
                />
                {errors.meeting_subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.meeting_subject.message}</p>
                )}
              </div>
            </div>

            {/* Toplantı açıklaması */}
            <div className="md:col-span-2">
              <label htmlFor="meeting_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Açıklama
              </label>
              <textarea
                id="meeting_description"
                rows={3}
                {...register('meeting_description')}
                className="input-field"
                placeholder="Toplantı açıklamasını girin"
              />
            </div>

            {/* Toplantı tarihi */}
            <div>
              <label htmlFor="meeting_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Toplantı Tarihi *
              </label>
              <input
                id="meeting_date"
                type="date"
                {...register('meeting_date', { required: 'Toplantı tarihi gerekli' })}
                className={`input-field ${errors.meeting_date ? 'input-error' : ''}`}
              />
              {errors.meeting_date && (
                <p className="mt-1 text-sm text-red-600">{errors.meeting_date.message}</p>
              )}
            </div>

            {/* Başlangıç ve bitiş saatleri */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="meeting_start_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Başlangıç Saati *
                </label>
                <input
                  id="meeting_start_time"
                  type="time"
                  {...register('meeting_start_time', { required: 'Başlangıç saati gerekli' })}
                  className={`input-field ${errors.meeting_start_time ? 'input-error' : ''}`}
                />
                {errors.meeting_start_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.meeting_start_time.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="meeting_end_time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bitiş Saati *
                </label>
                <input
                  id="meeting_end_time"
                  type="time"
                  {...register('meeting_end_time', { required: 'Bitiş saati gerekli' })}
                  className={`input-field ${errors.meeting_end_time ? 'input-error' : ''}`}
                />
                {errors.meeting_end_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.meeting_end_time.message}</p>
                )}
              </div>
            </div>

            {/* Toplantı yeri */}
            <div>
              <label htmlFor="meeting_location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Toplantı Yeri
              </label>
              <input
                id="meeting_location"
                type="text"
                {...register('meeting_location')}
                className="input-field"
                placeholder="Toplantı yerini girin"
              />
            </div>

            {/* Toplantı tipi */}
            <div>
              <label htmlFor="meeting_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Toplantı Tipi *
              </label>
              <select
                id="meeting_type"
                {...register('meeting_type', { required: 'Toplantı tipi gerekli' })}
                className={`input-field ${errors.meeting_type ? 'input-error' : ''}`}
              >
                <option value="">Tip seçin</option>
                <option value="Yüz Yüze">Yüz Yüze</option>
                <option value="Online">Online</option>
                <option value="Telefon">Telefon</option>
                <option value="Video Konferans">Video Konferans</option>
              </select>
              {errors.meeting_type && (
                <p className="mt-1 text-sm text-red-600">{errors.meeting_type.message}</p>
              )}
            </div>

            {/* Katılımcılar */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Katılımcılar
              </label>
              
              {/* Seçilen kullanıcılar */}
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedUsers.map((user) => (
                  <div key={user.id} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                    <span>{user.user_fullname}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Kullanıcı seçme dropdown */}
              <div className="relative user-dropdown">
                <button
                  type="button"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="flex items-center">
                    <FiPlus className="mr-2" size={16} />
                    Katılımcı Ekle
                  </span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showUserDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {users
                      .filter(user => !selectedUsers.find(u => u.id === user.id))
                      .map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleAddUser(user)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {user.user_fullname}
                        </button>
                      ))}
                    {users.filter(user => !selectedUsers.find(u => u.id === user.id)).length === 0 && (
                      <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                        Tüm kullanıcılar eklendi
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal footer */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Kaydediliyor...' : (meeting ? 'Güncelle' : 'Ekle')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerMeetingModal;
